import React, { useEffect, useRef } from 'react';
import styled from '@emotion/styled';

// components
import Typography from '../Typography';
import Icon from '../Icon';

// colors
import { primary, blue, gray, red, white, black } from '@styles/Colors';

// constants
import { Shadow } from '@styles/Shadow';
import { Radius } from '@styles/Radius';
import { TypoVariant } from '@constants/atoms';
import { MOBILE_MODE } from '@constants/size';
import { Keyframes } from '@constants/keyframes';
import Divider from '../Divider';

type Props = Styleable & {
  open: boolean;
  width?: string;
  title: string | React.ReactNode;
  subTitle?: string;
  variant?: TypoVariant;
  children: React.ReactNode;
  handleModalClose: () => void;
  modalRef?: any;
  padding?: string | number;
};

const ModalWrapper = styled.div`
  position: fixed;
  z-index: 999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  animation: ${Keyframes.slowOpacityWithScale} 0.15s forwards;
`;

const ModalBox = styled.div<{ width?: string; padding: string | number }>`
  width: ${(props) => props.width || '400px'};
  background: #fff;
  box-shadow: ${Shadow.MEDIUM};
  border-radius: ${Radius.MEDIUM};
  padding: ${({ padding }) => (typeof padding === 'number' ? `${padding}px` : padding)};

  display: grid;
  row-gap: 16px;

  @media ${MOBILE_MODE} {
    width: 320px;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const ModalContent = styled.div``;

export default function Modal({
  children,
  open,
  width,
  title,
  subTitle,
  variant = TypoVariant.SH3,
  handleModalClose,
  style,
  padding = 16,
}: Props) {
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const curModalRef = useRef<HTMLDivElement>(null);

  //modal 외부클릭(modalWrapper 클릭)시 modal닫기
  useEffect(() => {
    if (open) {
      const handleClickOutside = (event: MouseEvent) => {
        const isOutsideClick =
          modalWrapperRef.current &&
          modalWrapperRef.current.contains(event.target as Node) &&
          !curModalRef.current?.contains(event.target as Node);

        if (isOutsideClick) {
          handleModalClose();
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [open, curModalRef, modalWrapperRef]);

  //esc 키 모달닫기
  const escFunction = useRef((event: KeyboardEvent) => {});
  useEffect(() => {
    escFunction.current = (event) => {
      if (event.key === 'Escape') {
        handleModalClose();
      }
    };
    const onEscKeyDown = escFunction.current;
    window.addEventListener('keydown', onEscKeyDown);

    return () => {
      window.removeEventListener('keydown', onEscKeyDown);
    };
  }, []);

  if (open) {
    return (
      <ModalWrapper ref={modalWrapperRef}>
        <ModalBox width={width} ref={curModalRef} padding={padding} style={{ ...style }}>
          <ModalHeader>
            {typeof title === 'string' ? (
              subTitle ? (
                <ModalTitleWrapper>
                  <Typography variant={TypoVariant.SH1}>{title}</Typography>
                  <Typography variant={TypoVariant.B4}>{subTitle}</Typography>
                </ModalTitleWrapper>
              ) : (
                <Typography variant={variant} color={primary.gray}>
                  {title}
                </Typography>
              )
            ) : (
              title
            )}
            <Icon name="close" stroke={primary.gray} onClick={handleModalClose} style={{ marginBottom: 'auto' }} />
          </ModalHeader>
          <Divider />
          <ModalContent>{children}</ModalContent>
        </ModalBox>
      </ModalWrapper>
    );
  } else return <></>;
}
